import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'
import { HiOutlineMail } from 'react-icons/hi'
import { FcGoogle } from 'react-icons/fc'
import { useAuthStore } from '../stores/authStore'
import { motion } from 'framer-motion'
import { supabase } from '../lib/supabase'

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
})

type LoginForm = z.infer<typeof loginSchema>

export default function LoginPage() {
  const location = useLocation()
  const isSignupRoute = location.pathname === '/signup'
  const [isLoading, setIsLoading] = useState(false)
  const [isSignUp, setIsSignUp] = useState(isSignupRoute)
  const [showVerificationMessage, setShowVerificationMessage] = useState(false)
  const [signupEmail, setSignupEmail] = useState('')
  const navigate = useNavigate()
  const { login, isAuthenticated, user } = useAuthStore()
  
  // Prevent duplicate OAuth processing
  const processingOAuthRef = useRef(false)
  const processedUserIdRef = useRef<string | null>(null)
  const [isProcessingOAuth, setIsProcessingOAuth] = useState(false)

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('[Login] Already authenticated, redirecting...')
      if (user.onboardingCompleted === false) {
        navigate('/onboarding', { replace: true })
      } else {
        navigate('/library', { replace: true })
      }
    }
  }, [isAuthenticated, user, navigate])

  // Detect OAuth callback early to prevent form rendering
  const hasOAuthCallback = useMemo(() => {
    const hash = window.location.hash
    const searchParams = new URLSearchParams(window.location.search)
    return (hash && hash.includes('access_token')) || searchParams.has('code')
  }, [])

  // Update isSignUp when route changes
  useEffect(() => {
    setIsSignUp(isSignupRoute)
  }, [isSignupRoute])

  // Handle OAuth tokens in URL hash
  useEffect(() => {
    const handleOAuthHash = async () => {
      const hash = window.location.hash
      const searchParams = new URLSearchParams(window.location.search)
      
      console.log('[OAuth] Checking URL hash:', hash)
      console.log('[OAuth] Checking URL params:', window.location.search)
      
      // Check for error in URL
      const error = searchParams.get('error')
      const errorDescription = searchParams.get('error_description')
      
      if (error) {
        console.error('[OAuth] Error in URL:', error, errorDescription)
        toast.error(`OAuth error: ${errorDescription || error}`)
        return
      }
      
      if (hash && hash.includes('access_token')) {
        console.log('[OAuth] Found access_token in hash, processing...')
        
        if (!supabase) {
          console.error('[OAuth] Supabase client not available')
          return
        }

        try {
          // Let Supabase handle the hash automatically
          const { data, error } = await supabase.auth.getSession()
          console.log('[OAuth] Session after hash:', { data, error })
          
          if (error) {
            console.error('[OAuth] Error getting session from hash:', error)
            toast.error('Authentication failed')
          }
          
          // Clear the hash from URL
          window.history.replaceState(null, '', window.location.pathname)
        } catch (err) {
          console.error('[OAuth] Exception handling hash:', err)
        }
      }
    }
    
    handleOAuthHash()
  }, [])

  // Handle OAuth callback - Listen for auth state changes
  useEffect(() => {
    if (!supabase) {
      console.log('[OAuth] Supabase client not available, skipping auth listener')
      return
    }

    // Don't set up OAuth listener if already authenticated
    if (isAuthenticated) {
      console.log('[OAuth] Already authenticated, skipping OAuth listener')
      return
    }

    console.log('[OAuth] Setting up auth listener...')
    console.log('[OAuth] Current URL:', window.location.href)
    console.log('[OAuth] Has hash:', window.location.hash)

    // Only check session if we have OAuth callback indicators (hash or code)
    const hasOAuthIndicators = window.location.hash.includes('access_token') || 
                               new URLSearchParams(window.location.search).has('code')
    
    if (hasOAuthIndicators) {
      supabase.auth.getSession().then(({ data: { session }, error }) => {
        console.log('[OAuth] Current session check:', { session, error })
        if (session?.user && !isAuthenticated) {
          console.log('[OAuth] Found existing session, handling user...')
          handleOAuthUser(session.user)
        } else {
          console.log('[OAuth] No existing session found')
        }
      })
    }

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[OAuth] Auth state changed:', { event, session, user: session?.user })
      
      if (event === 'SIGNED_IN' && session?.user && !isAuthenticated) {
        const isOAuthCallback = window.location.hash.includes('access_token') || 
                                new URLSearchParams(window.location.search).has('code')
        
        if (isOAuthCallback) {
          console.log('[OAuth] SIGNED_IN event detected with OAuth callback, handling user...')
          handleOAuthUser(session.user)
        } else {
          console.log('[OAuth] SIGNED_IN event but no OAuth callback indicators, ignoring stale session')
        }
      } else if (event === 'SIGNED_OUT') {
        console.log('[OAuth] SIGNED_OUT event detected')
      } else {
        console.log('[OAuth] Other auth event:', event)
      }
    })

    return () => {
      console.log('[OAuth] Cleaning up auth listener')
      subscription.unsubscribe()
    }
  }, [navigate, login, isAuthenticated])

  const handleOAuthUser = async (user: any) => {
    // Prevent duplicate processing
    if (processingOAuthRef.current) {
      console.log('[OAuth] Already processing, skipping duplicate call')
      return
    }
    
    if (processedUserIdRef.current === user.id) {
      console.log('[OAuth] Already processed this user, skipping')
      return
    }
    
    if (isAuthenticated) {
      console.log('[OAuth] Already authenticated, skipping user handler')
      return
    }
    
    processingOAuthRef.current = true
    setIsProcessingOAuth(true)
    
    try {
      console.log('[OAuth] Handling user:', {
        id: user.id,
        email: user.email,
        metadata: user.user_metadata
      })
      
      console.log('[OAuth] Calling backend /api/auth/oauth-callback...')
      
      const response = await fetch('/api/auth/oauth-callback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          name: user.user_metadata?.full_name || user.email?.split('@')[0],
          provider: 'google',
          providerId: user.id
        }),
      })

      console.log('[OAuth] Backend response status:', response.status)
      
      const result = await response.json()
      console.log('[OAuth] Backend response data:', result)

      if (!response.ok) {
        throw new Error(result.message || 'OAuth callback failed')
      }

      console.log('[OAuth] Logging in with token...')
      
      processedUserIdRef.current = user.id
      login(result.token, result.user)

      if (result.user.onboardingCompleted === false) {
        console.log('[OAuth] Redirecting to onboarding...')
        navigate('/onboarding', { replace: true })
      } else {
        console.log('[OAuth] Redirecting to library...')
        navigate('/library', { replace: true })
      }
    } catch (error) {
      console.error('[OAuth] User handling error:', error)
      toast.error('Failed to complete sign in')
      setIsProcessingOAuth(false)
    } finally {
      processingOAuthRef.current = false
    }
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true)
    
    try {
      const endpoint = isSignUp ? '/api/auth/signup' : '/api/auth/login'
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })

      const result = await response.json()

      if (!response.ok) {
        if (result.requiresVerification) {
          setSignupEmail(data.email)
          setShowVerificationMessage(true)
          return
        }
        throw new Error(result.message || result.error || 'Authentication failed')
      }

      if (isSignUp) {
        setSignupEmail(data.email)
        setShowVerificationMessage(true)
        toast.success('Account created! Please check your email.')
        return
      }

      login(result.token, result.user)
      
      console.log('Login result:', {
        onboardingCompleted: result.user.onboardingCompleted,
        type: typeof result.user.onboardingCompleted,
        user: result.user
      })
      
      if (result.user.onboardingCompleted === false) {
        console.log('Redirecting to onboarding')
        navigate('/onboarding', { replace: true })
      } else {
        console.log('Redirecting to library')
        navigate('/library', { replace: true })
      }
      
    } catch (error) {
      console.error('Auth error:', error)
      toast.error(error instanceof Error ? error.message : 'Authentication failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    try {
      if (!supabase) {
        console.error('[OAuth] Supabase client not initialized')
        toast.error('Authentication not configured')
        return
      }

      console.log('[OAuth] Starting Google sign-in...')
      console.log('[OAuth] Current origin:', window.location.origin)
      console.log('[OAuth] Redirect URL will be:', `${window.location.origin}/login`)
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/login`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        },
      })

      console.log('[OAuth] signInWithOAuth response:', { data, error })

      if (error) {
        console.error('[OAuth] Error from signInWithOAuth:', error)
        throw error
      }

      console.log('[OAuth] Redirect should happen now...')
    } catch (error) {
      console.error('[OAuth] Google sign-in error:', error)
      toast.error('Failed to sign in with Google')
    }
  }

  // Show email verification message
  if (showVerificationMessage) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md text-center"
        >
          <div className="bg-white rounded-lg border border-gray-200 p-8">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <HiOutlineMail className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Check your email</h2>
            <p className="text-gray-600 mb-4">
              We've sent a verification link to
            </p>
            <p className="font-medium text-gray-900 mb-6">{signupEmail}</p>
            <p className="text-sm text-gray-500 mb-6">
              Click the link in the email to verify your account, then come back here to sign in.
            </p>
            <button
              onClick={() => {
                setShowVerificationMessage(false)
                navigate('/login')
              }}
              className="w-full bg-black text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors"
            >
              Back to Sign in
            </button>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex">
      {/* OAuth Processing Overlay */}
      {(isProcessingOAuth || hasOAuthCallback) && (
        <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-blue-400 animate-[bounce_1s_ease-in-out_infinite]" />
              <span className="w-3 h-3 rounded-full bg-blue-500 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.15s' }} />
              <span className="w-3 h-3 rounded-full bg-blue-600 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.3s' }} />
            </div>
            <p className="text-gray-600 font-medium">Completing sign in...</p>
          </div>
        </div>
      )}

      {/* Hide login form when processing OAuth */}
      {!isProcessingOAuth && !hasOAuthCallback && (
        <>
          {/* Left Panel - Subtle teal bg with full-size image */}
          <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-teal-50">
            {/* Logo + name top-left */}
            <div className="absolute top-8 left-8 z-20 flex items-center gap-1">
              <img 
                src="/memtraillogin.png" 
                alt="Memtrail" 
                className="w-10 h-10 object-contain"
              />
              <span className="font-bold text-2xl text-black" style={{ fontFamily: 'Outfit, sans-serif' }}>Memtrail</span>
            </div>

            {/* Full-size image */}
            <img 
              src="/logintrailimage.png" 
              alt="Memtrail" 
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>

          {/* Right Panel - Sign in form */}
          <div className="w-full lg:w-1/2 flex flex-col bg-white">
            {/* Mobile logo - only visible on small screens */}
            <div className="lg:hidden flex items-center gap-1 p-6">
              <img 
                src="/memtraillogin.png" 
                alt="Memtrail" 
                className="w-10 h-10 object-contain"
              />
              <span className="font-bold text-2xl text-black" style={{ fontFamily: 'Outfit, sans-serif' }}>Memtrail</span>
            </div>

            {/* Form container - centered vertically */}
            <div className="flex-1 flex items-center justify-center px-8 sm:px-12 lg:px-16">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-sm"
              >
                {/* Header */}
                <div className="mb-8">
                  <h1 className="text-2xl font-bold text-gray-900 mb-1">
                    {isSignUp ? 'Create account' : 'Sign in'}
                  </h1>
                  <p className="text-gray-500 text-sm">
                    {isSignUp ? 'Get started with Memtrail' : 'Welcome back to Memtrail'}
                  </p>
                </div>

                {/* Google Sign In */}
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors bg-white text-sm font-medium text-gray-700"
                >
                  <FcGoogle className="w-5 h-5" />
                  Continue with Google
                </button>

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-3 bg-white text-gray-400">or</span>
                  </div>
                </div>

                {/* Email/Password Form */}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email
                    </label>
                    <input
                      {...register('email')}
                      type="email"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-gray-900 text-sm placeholder:text-gray-400"
                      placeholder="name@example.com"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Password
                    </label>
                    <input
                      {...register('password')}
                      type="password"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-gray-900 text-sm placeholder:text-gray-400"
                      placeholder="Enter your password"
                    />
                    {errors.password && (
                      <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gray-900 text-white py-2.5 px-4 rounded-lg text-sm font-medium hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.15s' }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.3s' }} />
                        {isSignUp ? 'Creating account...' : 'Signing in...'}
                      </div>
                    ) : (
                      isSignUp ? 'Create account' : 'Sign in'
                    )}
                  </button>
                </form>

                {/* Toggle sign up / sign in */}
                <p className="mt-6 text-center text-sm text-gray-500">
                  {isSignUp ? (
                    <>Already have an account?{' '}
                      <button onClick={() => navigate('/login')} className="text-blue-600 font-medium hover:text-blue-700">
                        Sign in
                      </button>
                    </>
                  ) : (
                    <>No account?{' '}
                      <button onClick={() => navigate('/signup')} className="text-blue-600 font-medium hover:text-blue-700">
                        Sign up
                      </button>
                    </>
                  )}
                </p>
              </motion.div>
            </div>

            {/* Footer */}
            <div className="px-8 py-6 flex items-center justify-center gap-6 text-xs text-gray-400">
              <a href="https://memtrail.app" className="hover:text-gray-600 transition-colors">Home</a>
              <a href="https://memtrail.app/privacy" className="hover:text-gray-600 transition-colors">Privacy</a>
              <a href="https://memtrail.app/terms" className="hover:text-gray-600 transition-colors">Terms</a>
            </div>
          </div>
        </>
      )}
    </div>
  )
}